import React, { useState, useEffect, useMemo } from 'react';
import { 
  FileText, Download, Eye, Search, Calendar, Filter,
  ChevronLeft, ChevronRight, Printer, Mail, DollarSign,
  CheckCircle2, Clock, XCircle, Building, User
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../ui/table';
import { Separator } from '../../ui/separator';
import { db } from '../../../lib/db';
import { formatCurrency, getPayrollStatusColor, formatPayrollStatus } from '../../../lib/payrollCalculations';
import { formatDate, getMonthName, getCurrentMonth, getCurrentYear } from '../../../lib/dateUtils';
import { generatePayslipPDF } from '../../../lib/export';
import { toast } from 'sonner';

export default function PayslipViewer() {
  const [loading, setLoading] = useState(true);
  const [payslips, setPayslips] = useState([]);
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(getCurrentYear());
  const [statusFilter, setStatusFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [showPayslipModal, setShowPayslipModal] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [payslipsData, usersData] = await Promise.all([
        db.payslips.toArray(),
        db.users.toArray()
      ]);
      setPayslips(payslipsData);
      setUsers(usersData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const departments = useMemo(() => {
    const depts = new Set();
    users.forEach(u => u.profile?.department && depts.add(u.profile.department));
    return ['all', ...Array.from(depts)];
  }, [users]);

  const filteredPayslips = useMemo(() => {
    return payslips.filter(p => {
      const employee = users.find(u => u.id === p.userId);
      const matchesSearch = 
        employee?.profile?.firstName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        employee?.profile?.lastName?.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesMonth = p.month === selectedMonth;
      const matchesYear = p.year === selectedYear;
      const matchesStatus = statusFilter === 'all' || p.status === statusFilter;
      const matchesDepartment = departmentFilter === 'all' || employee?.profile?.department === departmentFilter;
      return matchesSearch && matchesMonth && matchesYear && matchesStatus && matchesDepartment;
    });
  }, [payslips, users, searchQuery, selectedMonth, selectedYear, statusFilter, departmentFilter]);

  const getEmployeeDetails = (userId) => {
    return users.find(u => u.id === userId);
  };

  const getEmployeeName = (userId) => {
    const employee = getEmployeeDetails(userId);
    return employee ? `${employee.profile?.firstName} ${employee.profile?.lastName}` : 'Unknown';
  };

  const viewPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setShowPayslipModal(true);
  };

  const downloadPayslip = (payslip) => {
    const employee = getEmployeeDetails(payslip.userId);
    try {
      generatePayslipPDF(payslip, employee);
      toast.success('Payslip PDF downloaded');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Error generating PDF');
    }
  };

  const generatePayslipText = (payslip, employee) => {
    const earnings = payslip.earnings || {};
    const taxes = payslip.taxes || {};
    const preTaxDeductions = payslip.preTaxDeductions || {};
    const ytd = payslip.ytd || {};
    
    return `
===============================================
              US PAYROLL PAYSLIP
===============================================
Company: PerformanceHub Inc.
Pay Period: ${getMonthName(payslip.month)} ${payslip.year}
Pay Frequency: ${payslip.payFrequency || 'Bi-Weekly'}
Generated: ${formatDate(payslip.generatedAt)}

EMPLOYEE DETAILS
-----------------------------------------------
Name: ${employee?.profile?.firstName} ${employee?.profile?.lastName}
Employee ID: ${payslip.userId?.substring(0, 8)}
Department: ${employee?.profile?.department}
Position: ${employee?.profile?.position}
Work State: ${payslip.workState || employee?.profile?.workState || 'N/A'}

EARNINGS
-----------------------------------------------
Regular Pay:           ${formatCurrency(earnings.regular || payslip.grossPay || 0)}
Overtime:              ${formatCurrency(earnings.overtime || 0)}
Bonus:                 ${formatCurrency(earnings.bonus || 0)}
Commission:            ${formatCurrency(earnings.commission || 0)}
Holiday Pay:           ${formatCurrency(earnings.holiday || 0)}
PTO:                   ${formatCurrency(earnings.pto || 0)}
-----------------------------------------------
GROSS PAY:             ${formatCurrency(payslip.grossPay || payslip.grossEarnings)}

PRE-TAX DEDUCTIONS
-----------------------------------------------
401(k):                ${formatCurrency(preTaxDeductions.retirement401k || 0)}
Health Insurance:      ${formatCurrency(preTaxDeductions.healthInsurance || 0)}
HSA:                   ${formatCurrency(preTaxDeductions.hsa || 0)}
FSA:                   ${formatCurrency(preTaxDeductions.fsa || 0)}
Dental:                ${formatCurrency(preTaxDeductions.dental || 0)}
Vision:                ${formatCurrency(preTaxDeductions.vision || 0)}
-----------------------------------------------
TOTAL PRE-TAX:         ${formatCurrency(payslip.totalPreTax || 0)}

TAXES
-----------------------------------------------
Federal Income Tax:    ${formatCurrency(taxes.federalIncome || 0)}
Social Security:       ${formatCurrency(taxes.socialSecurity || 0)}
Medicare:              ${formatCurrency(taxes.medicare || 0)}
State Tax (${taxes.stateName || payslip.workState || 'N/A'}):       ${formatCurrency(taxes.stateIncome || 0)}
SDI:                   ${formatCurrency(taxes.sdi || 0)}
-----------------------------------------------
TOTAL TAXES:           ${formatCurrency(payslip.totalTaxes || 0)}

===============================================
NET PAY:               ${formatCurrency(payslip.netPay)}
===============================================

YEAR-TO-DATE
-----------------------------------------------
Gross Pay:             ${formatCurrency(ytd.grossPay || 0)}
Federal Tax:           ${formatCurrency(ytd.federalTax || 0)}
Social Security:       ${formatCurrency(ytd.socialSecurity || 0)}
Medicare:              ${formatCurrency(ytd.medicare || 0)}
State Tax:             ${formatCurrency(ytd.stateTax || 0)}
401(k):                ${formatCurrency(ytd.retirement401k || 0)}
Net Pay:               ${formatCurrency(ytd.netPay || 0)}

This is a computer-generated US payroll statement.
    `.trim();
  };

  const goToPreviousMonth = () => {
    if (selectedMonth === 1) {
      setSelectedMonth(12);
      setSelectedYear(selectedYear - 1);
    } else {
      setSelectedMonth(selectedMonth - 1);
    }
  };

  const goToNextMonth = () => {
    if (selectedMonth === 12) {
      setSelectedMonth(1);
      setSelectedYear(selectedYear + 1);
    } else {
      setSelectedMonth(selectedMonth + 1);
    }
  };

  const totalGross = filteredPayslips.reduce((sum, p) => sum + (Number(p.grossPay) || Number(p.grossEarnings) || 0), 0);
  const totalNet = filteredPayslips.reduce((sum, p) => sum + (Number(p.netPay) || 0), 0);
  const totalDeductions = filteredPayslips.reduce((sum, p) => sum + (Number(p.totalDeductions) || ((Number(p.totalPreTax) || 0) + (Number(p.totalTaxes) || 0) + (Number(p.totalPostTax) || 0)) || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Payslips</h1>
          <p className="text-gray-500 mt-1">View and manage employee payslips</p>
        </div>
      </div>

      {/* Month Selector */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex items-center justify-center gap-4">
            <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900">
                {getMonthName(selectedMonth)} {selectedYear}
              </h2>
              <p className="text-sm text-gray-500">{filteredPayslips.length} payslips</p>
            </div>
            <Button variant="ghost" size="icon" onClick={goToNextMonth}>
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Gross</p>
                <p className="text-2xl font-bold text-gray-900">{formatCurrency(totalGross)}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Deductions</p>
                <p className="text-2xl font-bold text-red-600">{formatCurrency(totalDeductions)}</p>
              </div>
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-0 shadow-sm">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Net Pay</p>
                <p className="text-2xl font-bold text-emerald-600">{formatCurrency(totalNet)}</p>
              </div>
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="border-0 shadow-sm">
        <CardContent className="py-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <Input
                placeholder="Search by employee name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Department" />
              </SelectTrigger>
              <SelectContent>
                {departments.map(dept => (
                  <SelectItem key={dept} value={dept}>
                    {dept === 'all' ? 'All Departments' : dept}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-full sm:w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="generated">Generated</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Payslips Table */}
      <Card className="border-0 shadow-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead>Employee</TableHead>
                <TableHead>Department</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Net Pay</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-center">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredPayslips.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                    No payslips found for this period
                  </TableCell>
                </TableRow>
              ) : (
                filteredPayslips.map(payslip => {
                  const employee = getEmployeeDetails(payslip.userId);
                  return (
                    <TableRow key={payslip.id} className="hover:bg-gray-50">
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
                            {getEmployeeName(payslip.userId).split(' ').map(n => n[0]).join('')}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">{getEmployeeName(payslip.userId)}</p>
                            <p className="text-xs text-gray-500">{employee?.profile?.position}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-gray-50">
                          {employee?.profile?.department || 'N/A'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right font-medium">
                        {formatCurrency(payslip.grossPay || payslip.grossEarnings)}
                      </TableCell>
                      <TableCell className="text-right text-red-600">
                        -{formatCurrency(payslip.totalDeductions || ((payslip.totalPreTax || 0) + (payslip.totalTaxes || 0) + (payslip.totalPostTax || 0)))}
                      </TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">
                        {formatCurrency(payslip.netPay)}
                      </TableCell>
                      <TableCell>
                        <Badge className={getPayrollStatusColor(payslip.status)}>
                          {formatPayrollStatus(payslip.status)}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        <div className="flex justify-center gap-1">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => viewPayslip(payslip)}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => downloadPayslip(payslip)}
                          >
                            <Download className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payslip Detail Modal */}
      <Dialog open={showPayslipModal} onOpenChange={setShowPayslipModal}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5 text-teal-600" />
              Payslip Details
            </DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${getMonthName(selectedPayslip.month)} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>

          {selectedPayslip && (
            <div className="space-y-6">
              {/* Employee Info */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-14 h-14 bg-gradient-to-br from-violet-400 to-purple-500 rounded-full flex items-center justify-center text-white text-lg font-medium">
                  {getEmployeeName(selectedPayslip.userId).split(' ').map(n => n[0]).join('')}
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">{getEmployeeName(selectedPayslip.userId)}</p>
                  <p className="text-sm text-gray-500">
                    {getEmployeeDetails(selectedPayslip.userId)?.profile?.department} • 
                    {getEmployeeDetails(selectedPayslip.userId)?.profile?.position}
                  </p>
                  {selectedPayslip.workState && (
                    <p className="text-xs text-gray-400">Work State: {selectedPayslip.workState}</p>
                  )}
                </div>
                <div className="ml-auto">
                  <Badge className={getPayrollStatusColor(selectedPayslip.status)}>
                    {formatPayrollStatus(selectedPayslip.status)}
                  </Badge>
                </div>
              </div>

              {/* Earnings */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <DollarSign className="w-4 h-4 text-emerald-600" />
                  Earnings
                </h3>
                <div className="space-y-2 p-4 bg-emerald-50 rounded-lg">
                  {selectedPayslip.earnings?.regular > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Regular Pay</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.regular)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.overtime > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Overtime</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.overtime)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.bonus > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Bonus</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.bonus)}</span>
                    </div>
                  )}
                  {selectedPayslip.earnings?.commission > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Commission</span>
                      <span className="font-medium text-gray-900">{formatCurrency(selectedPayslip.earnings.commission)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Gross Pay</span>
                    <span className="text-emerald-600">{formatCurrency(selectedPayslip.grossPay || selectedPayslip.grossEarnings)}</span>
                  </div>
                </div>
              </div>

              {/* Pre-Tax Deductions */}
              {selectedPayslip.preTaxDeductions && Object.values(selectedPayslip.preTaxDeductions).some(v => v > 0) && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <XCircle className="w-4 h-4 text-violet-600" />
                    Pre-Tax Deductions
                  </h3>
                  <div className="space-y-2 p-4 bg-violet-50 rounded-lg">
                    {selectedPayslip.preTaxDeductions?.retirement401k > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">401(k)</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.retirement401k)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.healthInsurance > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Health Insurance</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.healthInsurance)}</span>
                      </div>
                    )}
                    {selectedPayslip.preTaxDeductions?.hsa > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">HSA</span>
                        <span className="font-medium text-violet-600">-{formatCurrency(selectedPayslip.preTaxDeductions.hsa)}</span>
                      </div>
                    )}
                    <Separator />
                    <div className="flex justify-between font-semibold">
                      <span>Total Pre-Tax</span>
                      <span className="text-violet-600">-{formatCurrency(selectedPayslip.totalPreTax)}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Taxes (US Style) */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <XCircle className="w-4 h-4 text-red-600" />
                  Taxes
                </h3>
                <div className="space-y-2 p-4 bg-red-50 rounded-lg">
                  {selectedPayslip.taxes?.federalIncome > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Federal Income Tax</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.federalIncome)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.socialSecurity > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Social Security (6.2%)</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.socialSecurity)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.medicare > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Medicare (1.45%)</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.medicare)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.stateIncome > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">State Tax ({selectedPayslip.taxes.stateName || selectedPayslip.workState})</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.stateIncome)}</span>
                    </div>
                  )}
                  {selectedPayslip.taxes?.sdi > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">SDI</span>
                      <span className="font-medium text-red-600">-{formatCurrency(selectedPayslip.taxes.sdi)}</span>
                    </div>
                  )}
                  <Separator />
                  <div className="flex justify-between font-semibold">
                    <span>Total Taxes</span>
                    <span className="text-red-600">-{formatCurrency(selectedPayslip.totalTaxes)}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="p-4 bg-gradient-to-r from-teal-500 to-emerald-600 rounded-lg text-white">
                <div className="flex justify-between items-center">
                  <span className="text-lg">Net Pay</span>
                  <span className="text-3xl font-bold">{formatCurrency(selectedPayslip.netPay)}</span>
                </div>
              </div>

              {/* YTD Summary */}
              {selectedPayslip.ytd && (
                <div className="p-4 bg-gray-50 rounded-lg">
                  <h4 className="font-medium text-gray-900 mb-2">Year-to-Date</h4>
                  <div className="grid grid-cols-2 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Gross</span>
                      <span className="font-medium">{formatCurrency(selectedPayslip.ytd.grossPay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Net</span>
                      <span className="font-medium">{formatCurrency(selectedPayslip.ytd.netPay)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Fed Tax</span>
                      <span className="font-medium">{formatCurrency(selectedPayslip.ytd.federalTax)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">SS + Medicare</span>
                      <span className="font-medium">{formatCurrency((selectedPayslip.ytd.socialSecurity || 0) + (selectedPayslip.ytd.medicare || 0))}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Meta Info */}
              <div className="text-sm text-gray-500 text-center">
                Generated on {formatDate(selectedPayslip.generatedAt)}
                {selectedPayslip.w4Info && (
                  <span className="ml-2">• Filing Status: {selectedPayslip.w4Info.filingStatus}</span>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPayslipModal(false)}>Close</Button>
            <Button 
              variant="outline"
              onClick={() => {
                window.print();
              }}
            >
              <Printer className="w-4 h-4 mr-2" /> Print
            </Button>
            <Button 
              onClick={() => downloadPayslip(selectedPayslip)}
              className="bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white"
            >
              <Download className="w-4 h-4 mr-2" /> Download
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
